import { parseEmailInboxPayload } from './email-inbox-payload';
import type {
  EmailInboxAttachment,
  EmailInboxMessage,
  EmailInboxPayload,
} from './email-inbox-payload';

const EMAIL_INBOX_JSON_PATH = 'emails/inbox.json';

type TraceFileLike = {
  name: string;
  content: string;
};

export function buildEmailInboxPayloadFromFiles({
  output,
  files,
}: {
  output: unknown;
  files: TraceFileLike[];
}): EmailInboxPayload | undefined {
  const directPayload = parseEmailInboxPayload(output);
  if (directPayload) {
    return enrichEmailInboxPayloadAttachments({
      payload: directPayload,
      files,
    });
  }

  const inboxFile = files.find((file) => file.name === EMAIL_INBOX_JSON_PATH);
  if (!inboxFile) {
    return undefined;
  }

  const inboxPayload = parseEmailInboxPayload(inboxFile.content);
  if (!inboxPayload) {
    return undefined;
  }

  return enrichEmailInboxPayloadAttachments({
    payload: inboxPayload,
    files,
  });
}

function enrichEmailInboxPayloadAttachments({
  payload,
  files,
}: {
  payload: EmailInboxPayload;
  files: TraceFileLike[];
}): EmailInboxPayload {
  const filesByName = new Map(files.map((file) => [file.name, file]));

  return {
    ...payload,
    messages: payload.messages.map((message) =>
      enrichMessageAttachments({ message, filesByName }),
    ),
  };
}

function enrichMessageAttachments({
  message,
  filesByName,
}: {
  message: EmailInboxMessage;
  filesByName: Map<string, TraceFileLike>;
}): EmailInboxMessage {
  const attachments = message.attachments ?? [];
  if (attachments.length === 0) {
    return message;
  }

  return {
    ...message,
    attachments: attachments.map((attachment) =>
      enrichAttachment({
        messageId: message.id,
        attachment,
        filesByName,
      }),
    ),
  };
}

function enrichAttachment({
  messageId,
  attachment,
  filesByName,
}: {
  messageId: string;
  attachment: EmailInboxAttachment;
  filesByName: Map<string, TraceFileLike>;
}): EmailInboxAttachment {
  if (attachment.contentBase64) {
    return attachment;
  }

  const attachmentFileName = `emails/attachments/${messageId}__${attachment.id}__${attachment.name}`;
  const matchingFile = filesByName.get(attachmentFileName);
  if (!matchingFile) {
    return attachment;
  }

  return {
    ...attachment,
    contentBase64: toContentBase64(matchingFile.content),
  };
}

function toContentBase64(cachedContent: string): string {
  const normalized = cachedContent.replace(/\s+/g, '');
  if (/^[A-Za-z0-9+/]+=*$/.test(normalized) && normalized.length % 4 === 0) {
    return normalized;
  }

  if (typeof btoa === 'function') {
    return btoa(unescape(encodeURIComponent(cachedContent)));
  }

  return normalized;
}
